'use client';

import { useState } from 'react';

import Icon from '@/components/ui/AppIcon';
import AppImage from '@/components/ui/AppImage';
import Header from '@/components/common/Header';
import Footer from '@/components/common/Footer';
import LandingEnquiryPopup from './components/LandingEnquiryPopup';

export default function LandingPage() {
  const [showEnquiryPopup, setShowEnquiryPopup] = useState(false);

  const courses = [
    {
      id: 'course_ai',
      title: 'AI Complete Course',
      description: 'Master Artificial Intelligence from basics to advanced. Learn Python, Machine Learning, Deep Learning, and build real-world AI applications. Includes 20+ AI Courses for image generation, video making, lesson learning and lots more.',
      duration: '6 Months',
      image: "https://img.rocket.new/generatedImages/rocket_gen_img_145acf251-1769555910174.png",
      alt: 'Student learning artificial intelligence and machine learning on laptop with neural network diagrams',
      icon: 'CpuChipIcon',
      gradient: 'from-blue-500 to-blue-600',
      highlights: ['Python Programming', 'Machine Learning', 'Deep Learning', 'Neural Networks', '20+ AI Tools', 'Real-world Projects']
    },
    {
      id: 'course_graphic',
      title: 'Graphic Designing',
      description: 'Learn professional graphic design with industry-standard tools. Master Adobe Photoshop, Illustrator, Figma and create stunning visual designs.',
      duration: '4 Months',
      image: "https://img.rocket.new/generatedImages/rocket_gen_img_10c8982dd-1767900720334.png",
      alt: 'Graphic designer working on creative illustrations using digital drawing tablet and design software',
      icon: 'PaintBrushIcon',
      gradient: 'from-purple-500 to-purple-600',
      highlights: ['Adobe Photoshop', 'Adobe Illustrator', 'Figma UI/UX', 'Logo Design', 'Branding', 'Portfolio Development']
    },
    {
      id: 'course_marketing',
      title: 'Digital Marketing',
      description: 'Become a digital marketing expert. Master SEO, Social Media Marketing, Google Ads, Email Marketing, and analytics to drive business growth.',
      duration: '3 Months',
      image: "https://img.rocket.new/generatedImages/rocket_gen_img_179a29bb3-1764669852473.png",
      alt: 'Digital marketing specialist analyzing social media analytics dashboard with engagement metrics and graphs',
      icon: 'MegaphoneIcon',
      gradient: 'from-orange-500 to-orange-600',
      highlights: ['SEO Mastery', 'Social Media Marketing', 'Google Ads', 'Email Marketing', 'Analytics', 'Content Strategy']
    },
    {
      id: 'course_coding',
      title: 'Coding',
      description: 'Learn to code from scratch. Master HTML, CSS, JavaScript, React, and build dynamic web applications with hands-on projects.',
      duration: '5 Months',
      image: "https://img.rocket.new/generatedImages/rocket_gen_img_1ade335f6-1766573583626.png",
      alt: 'Programmer writing JavaScript code on laptop with multiple code editor windows and terminal',
      icon: 'CodeBracketIcon',
      gradient: 'from-green-500 to-green-600',
      highlights: ['C, C++, Java', 'Python Programming', 'JavaScript & React', 'HTML & CSS', 'Web Development', 'Problem Solving']
    },
    {
      id: 'course_video',
      title: 'Video Editing',
      description: 'Master professional video editing with Adobe Premiere Pro and After Effects. Learn color grading, motion graphics, and storytelling.',
      duration: '3 Months',
      image: "https://images.unsplash.com/photo-1596091751528-ce0cd4a09f87",
      alt: 'Video editor working on timeline with multiple video clips in Adobe Premiere Pro software',
      icon: 'FilmIcon',
      gradient: 'from-red-500 to-red-600',
      highlights: ['Premiere Pro', 'After Effects', 'Color Grading', 'Motion Graphics', 'Sound Design', 'YouTube Optimization']
    },
    {
      id: 'course_web',
      title: 'Website Development',
      description: 'Build stunning websites with WordPress, Webflow, and custom coding. Learn both drag-and-drop builders and hand-coded development.',
      duration: '4 Months',
      image: "https://img.rocket.new/generatedImages/rocket_gen_img_1e72c49c5-1764656058198.png",
      alt: 'Web developer building responsive website layout with HTML CSS code and browser preview',
      icon: 'GlobeAltIcon',
      gradient: 'from-cyan-500 to-cyan-600',
      highlights: ['WordPress', 'Webflow', 'Custom Coding', 'Responsive Design', 'SEO', 'Deployment']
    },
    {
      id: 'course_app',
      title: 'App Building',
      description: 'Create mobile apps for iOS and Android. Learn React Native, Flutter, UI/UX design principles, and deploy apps to app stores.',
      duration: '6 Months',
      image: "https://img.rocket.new/generatedImages/rocket_gen_img_1f69208b2-1766925322115.png",
      alt: 'Mobile app development workspace showing smartphone displaying app interface with iOS and Android devices',
      icon: 'DevicePhoneMobileIcon',
      gradient: 'from-indigo-500 to-indigo-600',
      highlights: ['React Native', 'Flutter & Dart', 'UI/UX Design', 'API Integration', 'App Store Deploy', 'Push Notifications']
    }
  ];

  return (
    <>
      <Header />
      <main className="min-h-screen bg-background">
        {/* Hero Section with Discount Offer */}
        <section className="relative pt-32 pb-20 px-6 overflow-hidden bg-gradient-to-br from-primary via-secondary to-primary/80">
          {/* Background Pattern */}
          <div className="absolute inset-0 opacity-10">
            <div className="absolute inset-0" style={{
              backgroundImage: 'radial-gradient(circle at 2px 2px, white 1px, transparent 0)',
              backgroundSize: '40px 40px'
            }}></div>
          </div>

          {/* Floating Icons */}
          <div className="absolute inset-0 pointer-events-none">
            <Icon name="SparklesIcon" size={60} className="absolute top-20 left-10 text-white/20 animate-pulse delay-100" />
            <Icon name="AcademicCapIcon" size={70} className="absolute top-40 right-20 text-white/20 animate-pulse delay-300" />
            <Icon name="RocketLaunchIcon" size={65} className="absolute bottom-32 left-20 text-white/20 animate-pulse delay-500" />
            <Icon name="TrophyIcon" size={55} className="absolute bottom-20 right-32 text-white/20 animate-pulse delay-700" />
          </div>

          <div className="relative z-10 max-w-7xl mx-auto text-center">
            {/* Discount Badge */}
            <div className="inline-flex items-center gap-2 bg-yellow-400 text-gray-900 rounded-full px-6 py-3 mb-6 shadow-xl animate-bounce">
              <Icon name="SparklesIcon" size={20} className="text-gray-900" />
              <span className="text-sm font-bold uppercase tracking-wider">
                🎉 Limited Time Offer: 10% OFF on Instant Enquiry!
              </span>
            </div>

            {/* Main Heading */}
            <h1 className="font-display text-5xl md:text-7xl lg:text-8xl uppercase tracking-tighter leading-none text-white mb-6">
              Transform Your<br />
              <span className="text-yellow-300">Career</span> Today
            </h1>

            {/* Motivational Subheading */}
            <p className="text-xl md:text-2xl text-white/90 max-w-3xl mx-auto mb-8 leading-relaxed">
              Don't just dream about success—<span className="text-yellow-300 font-semibold">build it</span>. Learn industry-ready skills that companies are hiring for right now.
            </p>

            {/* Key Benefits */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-4xl mx-auto mb-10">
              <div className="bg-white/10 backdrop-blur-md border border-white/20 rounded-2xl p-6">
                <Icon name="AcademicCapIcon" size={40} className="text-yellow-300 mx-auto mb-3" />
                <h3 className="text-white font-semibold text-lg mb-2">Expert Training</h3>
                <p className="text-white/80 text-sm">Learn from industry professionals with 5+ years experience</p>
              </div>
              <div className="bg-white/10 backdrop-blur-md border border-white/20 rounded-2xl p-6">
                <Icon name="BriefcaseIcon" size={40} className="text-yellow-300 mx-auto mb-3" />
                <h3 className="text-white font-semibold text-lg mb-2">100% Job-Oriented</h3>
                <p className="text-white/80 text-sm">Practical training with real-world projects and placements</p>
              </div>
              <div className="bg-white/10 backdrop-blur-md border border-white/20 rounded-2xl p-6">
                <Icon name="UserGroupIcon" size={40} className="text-yellow-300 mx-auto mb-3" />
                <h3 className="text-white font-semibold text-lg mb-2">1000+ Success Stories</h3>
                <p className="text-white/80 text-sm">Join our community of successful graduates</p>
              </div>
            </div>

            {/* CTA Buttons */}
            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
              <button
                onClick={() => setShowEnquiryPopup(true)}
                className="group relative inline-flex items-center gap-2 bg-yellow-400 text-gray-900 px-10 py-5 rounded-full text-lg font-bold uppercase tracking-wider hover:bg-yellow-300 transition-all shadow-2xl hover:shadow-yellow-400/50 hover:scale-105"
              >
                Get 10% OFF - Enquire Now!
                <Icon name="ArrowRightIcon" size={24} className="group-hover:translate-x-1 transition-transform" />
              </button>
              <button
                onClick={() => setShowEnquiryPopup(true)}
                className="inline-flex items-center gap-2 bg-white/10 backdrop-blur-md text-white border-2 border-white/30 px-10 py-5 rounded-full text-lg font-bold uppercase tracking-wider hover:bg-white/20 transition-all"
              >
                <Icon name="PhoneIcon" size={24} />
                Book Free Counselling
              </button>
            </div>
          </div>
        </section>

        {/* Motivational Quote Section */}
        <section className="py-16 px-6 bg-gradient-to-r from-secondary/10 to-primary/10">
          <div className="max-w-5xl mx-auto text-center">
            <blockquote className="font-display text-3xl md:text-4xl text-foreground mb-6 leading-relaxed">
              "The future belongs to those who learn more skills and combine them in creative ways."
            </blockquote>
            <p className="text-xl text-muted-foreground font-semibold">Start Your Learning Journey Today</p>
          </div>
        </section>

        {/* Free Demo & Counselling Banner */}
        <section className="py-12 px-6 bg-gradient-to-r from-green-600 to-teal-600">
          <div className="max-w-6xl mx-auto">
            <div className="grid md:grid-cols-2 gap-8">
              <div className="bg-white/10 backdrop-blur-md border border-white/20 rounded-2xl p-8 text-center">
                <Icon name="VideoCameraIcon" size={56} className="text-white mx-auto mb-4" />
                <h3 className="font-display text-3xl text-white mb-3 uppercase">Free Demo Lecture</h3>
                <p className="text-white/90 mb-6 text-lg">Experience our teaching methodology before you enroll. No commitment required!</p>
                <button
                  onClick={() => setShowEnquiryPopup(true)}
                  className="inline-flex items-center gap-2 bg-white text-green-600 px-8 py-4 rounded-full text-base font-bold uppercase hover:bg-gray-100 transition-all shadow-xl"
                >
                  <Icon name="PlayIcon" size={20} />
                  Book Free Demo
                </button>
              </div>
              <div className="bg-white/10 backdrop-blur-md border border-white/20 rounded-2xl p-8 text-center">
                <Icon name="ChatBubbleLeftRightIcon" size={56} className="text-white mx-auto mb-4" />
                <h3 className="font-display text-3xl text-white mb-3 uppercase">Free Counselling</h3>
                <p className="text-white/90 mb-6 text-lg">Get personalized career guidance from our experts. Choose the right course for your goals!</p>
                <button
                  onClick={() => setShowEnquiryPopup(true)}
                  className="inline-flex items-center gap-2 bg-white text-teal-600 px-8 py-4 rounded-full text-base font-bold uppercase hover:bg-gray-100 transition-all shadow-xl"
                >
                  <Icon name="PhoneIcon" size={20} />
                  Get Free Counselling
                </button>
              </div>
            </div>
          </div>
        </section>

        {/* All Courses Section */}
        <section className="py-20 px-6 bg-background">
          <div className="max-w-7xl mx-auto">
            {/* Section Header */}
            <div className="text-center mb-16">
              <span className="inline-flex items-center gap-2 bg-primary/10 border border-primary/20 rounded-full px-4 py-2 mb-6">
                <Icon name="AcademicCapIcon" size={16} className="text-primary" />
                <span className="text-xs font-semibold uppercase tracking-wider text-primary">
                  Our Courses
                </span>
              </span>
              <h2 className="font-display text-4xl md:text-6xl uppercase tracking-tighter text-foreground mb-4">
                Choose Your Path to Success
              </h2>
              <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
                Industry-ready courses designed to make you job-ready. All courses include practical training, live projects, and placement assistance.
              </p>
            </div>

            {/* Courses Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {courses.map((course) => (
                <div
                  key={course.id}
                  className="group bg-card border border-border rounded-2xl overflow-hidden hover:border-primary/50 hover:shadow-2xl transition-all duration-300"
                >
                  {/* Course Image */}
                  <div className="relative h-56 overflow-hidden">
                    <AppImage
                      src={course.image}
                      alt={course.alt}
                      className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                    />
                    <div className={`absolute inset-0 bg-gradient-to-br ${course.gradient} opacity-20 group-hover:opacity-40 transition-opacity`}></div>
                    
                    {/* Icon Overlay */}
                    <div className={`absolute top-4 right-4 w-14 h-14 rounded-xl bg-gradient-to-br ${course.gradient} flex items-center justify-center shadow-lg`}>
                      <Icon name={course.icon as any} size={28} className="text-white" />
                    </div>

                    {/* Duration Badge */}
                    <div className="absolute bottom-4 left-4 bg-white/90 backdrop-blur-sm rounded-full px-4 py-2 flex items-center gap-2">
                      <Icon name="ClockIcon" size={16} className="text-primary" />
                      <span className="text-sm font-semibold text-foreground">{course.duration}</span>
                    </div>
                  </div>

                  {/* Course Content */}
                  <div className="p-6">
                    <h3 className="font-display text-2xl uppercase tracking-tight text-foreground mb-3">
                      {course.title}
                    </h3>
                    <p className="text-base text-muted-foreground leading-relaxed mb-4">
                      {course.description}
                    </p>

                    {/* Course Highlights */}
                    <div className="bg-muted/20 rounded-xl p-4 mb-4">
                      <h4 className="text-xs font-semibold uppercase tracking-wider text-foreground mb-3">
                        What You'll Learn
                      </h4>
                      <div className="grid grid-cols-2 gap-2">
                        {course.highlights.map((highlight, index) => (
                          <div key={`${course.id}_highlight_${index}`} className="flex items-center gap-2">
                            <Icon name="CheckCircleIcon" size={16} className="text-primary flex-shrink-0" />
                            <span className="text-xs text-muted-foreground">{highlight}</span>
                          </div>
                        ))}
                      </div>
                    </div>

                    {/* Enquire Button */}
                    <button
                      onClick={() => setShowEnquiryPopup(true)}
                      className={`w-full bg-gradient-to-r ${course.gradient} text-white py-3 rounded-xl font-semibold uppercase tracking-wider hover:shadow-lg transition-all flex items-center justify-center gap-2 group`}
                    >
                      Enquire Now - Get 10% OFF
                      <Icon name="ArrowRightIcon" size={18} className="group-hover:translate-x-1 transition-transform" />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Why Choose Us Section */}
        <section className="py-20 px-6 bg-gradient-to-br from-primary/5 to-secondary/5">
          <div className="max-w-7xl mx-auto">
            <div className="text-center mb-16">
              <h2 className="font-display text-4xl md:text-6xl uppercase tracking-tighter text-foreground mb-4">
                Why Apollobits Academy?
              </h2>
              <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
                We don't just teach—we transform careers. Here's what makes us different.
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
              <div className="bg-card border border-border rounded-2xl p-6 text-center hover:border-primary/50 transition-all">
                <div className="w-16 h-16 bg-gradient-to-br from-blue-500 to-blue-600 rounded-2xl flex items-center justify-center mx-auto mb-4">
                  <Icon name="UserGroupIcon" size={32} className="text-white" />
                </div>
                <h3 className="font-semibold text-lg text-foreground mb-2">1000+ Students Trained</h3>
                <p className="text-sm text-muted-foreground">Join our growing community of successful professionals</p>
              </div>

              <div className="bg-card border border-border rounded-2xl p-6 text-center hover:border-primary/50 transition-all">
                <div className="w-16 h-16 bg-gradient-to-br from-green-500 to-green-600 rounded-2xl flex items-center justify-center mx-auto mb-4">
                  <Icon name="BriefcaseIcon" size={32} className="text-white" />
                </div>
                <h3 className="font-semibold text-lg text-foreground mb-2">100% Job-Oriented</h3>
                <p className="text-sm text-muted-foreground">Practical training with placement assistance</p>
              </div>

              <div className="bg-card border border-border rounded-2xl p-6 text-center hover:border-primary/50 transition-all">
                <div className="w-16 h-16 bg-gradient-to-br from-purple-500 to-purple-600 rounded-2xl flex items-center justify-center mx-auto mb-4">
                  <Icon name="AcademicCapIcon" size={32} className="text-white" />
                </div>
                <h3 className="font-semibold text-lg text-foreground mb-2">Expert Trainers</h3>
                <p className="text-sm text-muted-foreground">Learn from industry professionals with 5+ years experience</p>
              </div>

              <div className="bg-card border border-border rounded-2xl p-6 text-center hover:border-primary/50 transition-all">
                <div className="w-16 h-16 bg-gradient-to-br from-orange-500 to-orange-600 rounded-2xl flex items-center justify-center mx-auto mb-4">
                  <Icon name="TrophyIcon" size={32} className="text-white" />
                </div>
                <h3 className="font-semibold text-lg text-foreground mb-2">5+ Years Experience</h3>
                <p className="text-sm text-muted-foreground">Trusted by students since 2020</p>
              </div>
            </div>
          </div>
        </section>

        {/* Final CTA Section */}
        <section className="py-20 px-6 bg-gradient-to-br from-primary via-secondary to-primary">
          <div className="max-w-4xl mx-auto text-center">
            <Icon name="SparklesIcon" size={64} className="text-yellow-300 mx-auto mb-6 animate-pulse" />
            <h2 className="font-display text-4xl md:text-6xl uppercase tracking-tighter text-white mb-6">
              Ready to Start Your Journey?
            </h2>
            <p className="text-xl text-white/90 mb-8 leading-relaxed">
              Don't miss out on this limited-time offer! Get <span className="text-yellow-300 font-bold">10% OFF</span> on your admission when you enquire now.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
              <button
                onClick={() => setShowEnquiryPopup(true)}
                className="group inline-flex items-center gap-2 bg-yellow-400 text-gray-900 px-10 py-5 rounded-full text-lg font-bold uppercase tracking-wider hover:bg-yellow-300 transition-all shadow-2xl hover:shadow-yellow-400/50 hover:scale-105"
              >
                Claim Your 10% Discount Now!
                <Icon name="ArrowRightIcon" size={24} className="group-hover:translate-x-1 transition-transform" />
              </button>
            </div>
            <p className="text-white/80 mt-6 text-sm">✓ Free Demo Lecture  ✓ Free Career Counselling  ✓ 100% Job-Oriented Training</p>
          </div>
        </section>
      </main>
      <Footer />

      {/* Enquiry Popup */}
      {showEnquiryPopup && (
        <LandingEnquiryPopup onClose={() => setShowEnquiryPopup(false)} />
      )}
    </>
  );
}